import {
	FirebaseMessaging,
	type Notification,
} from "@capacitor-firebase/messaging"
import posthog from "posthog-js"
import { detectPlatform } from "./detect"

/**
 * Notifications the user still sees in the system shade. Each push carries our own
 * `notification_id`, so a delivered notification can be matched back to the row
 * it came from without the app keeping a table of its own. iOS keeps it in the
 * notification data; Android hides the FCM data payload in the shade, so there the
 * backend repeats the id as the notification tag.
 */
async function deliveredWithIds(): Promise<
	{ id: number; notification: Notification }[]
> {
	const platform = detectPlatform()
	if (platform !== "android" && platform !== "ios") return []

	try {
		const { notifications } =
			await FirebaseMessaging.getDeliveredNotifications()
		const matched: { id: number; notification: Notification }[] = []
		for (const notification of notifications) {
			const id = readNotificationId(notification, platform)
			if (id !== null) matched.push({ id, notification })
		}
		return matched
	} catch (error) {
		posthog.captureException(error, { scope: "push.delivered" })
		return []
	}
}

// Everything in an FCM data payload arrives as a string.
function readNotificationId(
	notification: Notification,
	platform: "android" | "ios",
): number | null {
	if (platform === "ios") {
		const data = notification.data as { notification_id?: string } | undefined
		return parseId(data?.notification_id)
	}
	return parseId(notification.tag)
}

function parseId(raw: string | undefined): number | null {
	if (raw === undefined || !/^\d+$/.test(raw)) return null
	const id = Number(raw)
	if (!Number.isSafeInteger(id)) return null
	return id
}

async function remove(notifications: Notification[]): Promise<void> {
	if (notifications.length === 0) return

	try {
		await FirebaseMessaging.removeDeliveredNotifications({ notifications })
	} catch (error) {
		posthog.captureException(error, { scope: "push.remove" })
	}
}

/**
 * Takes one notification out of the system shade. Called when its row is
 * answered, however the user got there — a tap on the push, or opening the
 * screen inside the app.
 */
export async function removeSystemNotification(
	notificationId: number,
): Promise<void> {
	const delivered = await deliveredWithIds()
	await remove(
		delivered
			.filter((entry) => entry.id === notificationId)
			.map((entry) => entry.notification),
	)
}

/**
 * Leaves in the shade only what the server still counts as pending. Runs off the
 * queue the server sends after a reconnect, which is also what a return from the
 * background produces — so notifications answered on another device, or answered
 * before the app was last killed, go away too.
 *
 * Only the notifications that are ours and no longer pending are removed:
 * everything else in the shade, including local notifications, is left alone.
 */
export async function keepPendingSystemNotifications(
	pendingIds: readonly number[],
): Promise<void> {
	const pending = new Set(pendingIds)
	const delivered = await deliveredWithIds()
	await remove(
		delivered
			.filter((entry) => !pending.has(entry.id))
			.map((entry) => entry.notification),
	)
}
